import { Toggle } from "../Toggle";
import { t } from "../../i18n";
import { useEffect, useState } from "react";
import {
  Check,
  KeyRound,
  LoaderCircle,
  RefreshCw,
  Save,
  X,
} from "lucide-react";
import type { CustomProviderDefinition, Provider } from "@lumen/shared";
import { api } from "../../api";
import { ConfigFields, normalizeSchema } from "../tools/ToolConfigFields";

const OPENCODE_BASE_URLS: Record<string, string> = {
  "opencode-go": "https://opencode.ai/zen/go/v1",
  "opencode-zen": "https://opencode.ai/zen/v1",
};
const ANTHROPIC_BASE_URL = "https://api.anthropic.com/v1";
const MISTRAL_BASE_URL = "https://api.mistral.ai/v1";
const OPENROUTER_BASE_URL = "https://openrouter.ai/api/v1";
const OLLAMA_BASE_URL = "http://localhost:11434";
const COMMANDCODE_BASE_URL = "https://api.commandcode.ai/provider/v1";
const GAME_ASSET_BASE_URLS: Record<string, string> = {
  meshy: "https://api.meshy.ai/openapi/v2",
  scenario: "https://api.cloud.scenario.com/v1",
  layer: "https://api.app.layer.ai/api/v1/workspaces",
  rodin: "https://api.hyper3d.com/api/v2",
  tripo: "https://api.tripo3d.ai/v2/openapi",
  wavespeed: "https://api.wavespeed.ai/api/v3",
  "retro-diffusion": "https://api.replicate.com/v1",
};

type ProviderForm = {
  name: string;
  type:
    | "openai-compatible"
    | "openrouter"
    | "ollama"
    | "anthropic"
    | "mistral"
    | "opencode-go"
    | "opencode-zen"
    | "codex-subscription"
    | "claude-code-subscription"
    | "claude-agent"
    | "commandcode"
    | "meshy"
    | "scenario"
    | "layer"
    | "rodin"
    | "tripo"
    | "wavespeed"
    | "retro-diffusion"
    | "custom";
  customProviderId: string;
  baseUrl: string;
  apiKey: string;
  instanceConfig: Record<string, unknown>;
  policy: "all" | "regex" | "selected";
  modelRegex: string;
  selectedModels: string[];
  filterCapabilities: ("vision" | "imageGeneration" | "tools")[];
  filterOutputModalities: string[];
  enabled: boolean;
  visibility: "platform" | "organization" | "personal";
};
const empty: ProviderForm = {
  name: "",
  type: "openai-compatible",
  customProviderId: "",
  baseUrl: "",
  apiKey: "",
  instanceConfig: {},
  policy: "all",
  modelRegex: "",
  selectedModels: [],
  filterCapabilities: [],
  filterOutputModalities: [],
  enabled: true,
  visibility: "personal",
};

/**
 * Builds the instanceConfig payload for a custom provider from the editor
 * form. Secret fields are only sent when the user typed a new value (a blank
 * secret means "keep the saved value" on edit); non-secret values are sent
 * as-is so optional fields can be cleared. An explicit `null` (the "Clear
 * saved" affordance in ConfigFields) is the clear sentinel the API's PATCH
 * merge treats as "remove the saved value entirely".
 */
function instanceConfigFromForm(
  definition: CustomProviderDefinition,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const schema = normalizeSchema(definition.inputSchema);
  const out: Record<string, unknown> = {};
  for (const [name, property] of Object.entries(schema.properties ?? {})) {
    const value = values[name];
    if (property.secret) {
      if (value !== undefined && value !== "") out[name] = value;
    } else if (value !== undefined) {
      out[name] = value;
    }
  }
  return out;
}

export function ProviderEditor({
  provider,
  scope,
  organizationId,
  availableScopes = [scope === "all" ? "personal" : scope],
  onClose,
  onSaved,
}: {
  provider?: Provider;
  scope: "platform" | "personal" | "organization" | "all";
  organizationId?: string;
  availableScopes?: Array<"platform" | "personal" | "organization">;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ProviderForm>(
    provider
      ? {
          name: provider.name,
          type: provider.type,
          customProviderId: provider.customProviderId ?? "",
          baseUrl: provider.baseUrl,
          apiKey: "",
          instanceConfig: provider.instanceConfig
            ? { ...provider.instanceConfig }
            : {},
          policy: provider.modelPolicy,
          modelRegex: provider.modelRegex ?? "",
          selectedModels: provider.selectedModels ?? [],
          filterCapabilities: provider.filterCapabilities ?? [],
          filterOutputModalities: provider.filterOutputModalities ?? [],
          enabled: provider.enabled,
          visibility: provider.scope ?? (scope === "all" ? "personal" : scope),
        }
      : { ...empty, visibility: scope === "all" ? "personal" : scope },
  );
  const [customDefinitions, setCustomDefinitions] = useState<
    CustomProviderDefinition[]
  >([]);
  useEffect(() => {
    void api<CustomProviderDefinition[]>("/custom-providers")
      .then(setCustomDefinitions)
      .catch(() => setCustomDefinitions([]));
  }, []);
  const selectedDefinition = customDefinitions.find(
    (definition) => definition.id === form.customProviderId,
  );
  // Disabled ("Draft") definitions are not selectable for new providers; they
  // only appear when editing a provider that already references one.
  const customOptions = customDefinitions.filter(
    (definition) =>
      definition.enabled || provider?.customProviderId === definition.id,
  );
  const [discovered, setDiscovered] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [codexLogin, setCodexLogin] = useState<{
    verificationUrl?: string;
    userCode?: string;
  } | null>(null);
  const [savedCodexId, setSavedCodexId] = useState<string | null>(null);
  const [savedClaudeCodeId, setSavedClaudeCodeId] = useState<string | null>(
    null,
  );
  const [claudeCodeLogin, setClaudeCodeLogin] = useState<{
    verificationUrl?: string;
    userCode?: string;
  } | null>(null);
  const [claudeLoginCode, setClaudeLoginCode] = useState("");
  const discover = async () => {
    if (!provider) return;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ models: Array<string | { id: string }> }>(
        `/providers/${provider.id}/discover`,
        { method: "POST" },
      );
      setDiscovered(
        result.models.map((model) =>
          typeof model === "string" ? model : model.id,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Discovery failed");
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body = {
        name: form.name,
        type: form.type,
        ...(form.type === "custom"
          ? {
              customProviderId: form.customProviderId,
              ...(selectedDefinition
                ? {
                    instanceConfig: instanceConfigFromForm(
                      selectedDefinition,
                      form.instanceConfig,
                    ),
                  }
                : {}),
            }
          : {}),
        baseUrl:
          form.type === "custom" ||
          form.type === "codex-subscription" ||
          form.type === "claude-code-subscription"
            ? undefined
            : form.type.startsWith("opencode") && !form.baseUrl
              ? OPENCODE_BASE_URLS[form.type]
              : form.type === "openrouter" && !form.baseUrl
                ? OPENROUTER_BASE_URL
                : form.type === "ollama" && !form.baseUrl
                  ? OLLAMA_BASE_URL
                  : (form.type === "anthropic" ||
                        form.type === "claude-agent") &&
                      !form.baseUrl
                    ? ANTHROPIC_BASE_URL
                    : form.type === "mistral" && !form.baseUrl
                      ? MISTRAL_BASE_URL
                      : form.type === "commandcode" && !form.baseUrl
                        ? COMMANDCODE_BASE_URL
                        : GAME_ASSET_BASE_URLS[form.type] && !form.baseUrl
                          ? GAME_ASSET_BASE_URLS[form.type]
                          : form.baseUrl,
        ...(form.apiKey && form.type !== "custom"
          ? { apiKey: form.apiKey }
          : {}),
        policy: form.policy,
        modelRegex: form.modelRegex || null,
        selectedModels: form.selectedModels,
        filterCapabilities: form.filterCapabilities,
        filterOutputModalities: form.filterOutputModalities,
        enabled: form.enabled,
        scope: form.visibility,
        ...(form.visibility === "organization" && organizationId
          ? { organizationId }
          : {}),
      };
      const saved = await api<Provider>(
        provider ? `/providers/${provider.id}` : "/providers",
        { method: provider ? "PATCH" : "POST", body: JSON.stringify(body) },
      );
      if (form.type === "codex-subscription") {
        const login = await api<{
          verificationUrl?: string;
          userCode?: string;
        }>(`/providers/${saved.id}/codex/login`, { method: "POST" });
        setSavedCodexId(saved.id);
        setCodexLogin(login);
      } else if (form.type === "claude-code-subscription") {
        const login = await api<{
          verificationUrl?: string;
          userCode?: string;
        }>(`/providers/${saved.id}/claude-code/login`, { method: "POST" });
        setSavedClaudeCodeId(saved.id);
        setClaudeCodeLogin(login);
      } else {
        await api(`/providers/${saved.id}/sync`, { method: "POST" });
        onSaved();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Provider could not be saved");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel mb-6 p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">
          {provider
            ? `Edit ${provider.name}`
            : form.type === "claude-code-subscription"
              ? "New Claude Code subscription"
              : form.type === "codex-subscription"
                ? "New Codex subscription"
                : form.type === "claude-agent"
                  ? "New Claude Agent provider"
                  : form.type === "openrouter"
                    ? "New OpenRouter provider"
                    : form.type.startsWith("opencode")
                      ? "New OpenCode provider"
                      : form.type === "ollama"
                        ? "New Ollama provider"
                        : form.type === "anthropic"
                          ? "New Anthropic provider"
                          : form.type === "mistral"
                            ? "New Mistral provider"
                            : [
                                  "meshy",
                                  "scenario",
                                  "layer",
                                  "rodin",
                                  "tripo",
                                  "wavespeed",
                                  "retro-diffusion",
                                ].includes(form.type)
                              ? "New game asset provider"
                              : form.type === "custom"
                                ? "New custom provider"
                                : "New OpenAI-compatible provider"}
        </h3>
        <button className="button p-1.5" onClick={onClose}>
          <X size={17} />
        </button>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label>
          <span className="label">{t("Visibility")}</span>
          <select
            className="field"
            value={form.visibility}
            disabled={
              form.type === "claude-agent" ||
              form.type === "claude-code-subscription"
            }
            onChange={(event) =>
              setForm({
                ...form,
                visibility: event.target.value as ProviderForm["visibility"],
              })
            }
          >
            {(form.type === "claude-agent" ||
            form.type === "claude-code-subscription"
              ? availableScopes.filter((value) => value === "personal")
              : availableScopes
            ).map((value) => (
              <option key={value} value={value}>
                {value === "platform"
                  ? t("Global")
                  : value === "organization"
                    ? t("Organization")
                    : t("Personal")}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="label">{t("Name")}</span>
          <input
            className="field"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>
        <label>
          <span className="label">{t("Provider type")}</span>
          <select
            className="field"
            value={form.type}
            onChange={(e) => {
              const value = e.target.value;
              // Re-selecting the "Custom — <name>" option (value "custom")
              // keeps the currently selected definition instead of clearing
              // it, which would disable Save.
              if (value === "custom") return;
              const definition = customDefinitions.find(
                (item) => item.id === value,
              );
              if (definition) {
                setForm((prev) => ({
                  ...prev,
                  type: "custom",
                  customProviderId: definition.id,
                  baseUrl: "",
                  instanceConfig:
                    provider?.customProviderId === definition.id
                      ? { ...(provider.instanceConfig ?? {}) }
                      : {},
                }));
                return;
              }
              const type = value as Exclude<ProviderForm["type"], "custom">;
              setForm((prev) => ({
                ...prev,
                type,
                visibility:
                  type === "claude-agent" || type === "claude-code-subscription"
                    ? "personal"
                    : prev.visibility,
                customProviderId: "",
                instanceConfig: {},
                baseUrl:
                  type === "codex-subscription" ||
                  type === "claude-code-subscription"
                    ? ""
                    : type.startsWith("opencode")
                      ? OPENCODE_BASE_URLS[type]
                      : type === "openrouter"
                        ? OPENROUTER_BASE_URL
                        : type === "ollama"
                          ? OLLAMA_BASE_URL
                          : type === "anthropic" || type === "claude-agent"
                            ? ANTHROPIC_BASE_URL
                            : type === "mistral"
                              ? MISTRAL_BASE_URL
                              : type === "commandcode"
                                ? COMMANDCODE_BASE_URL
                                : GAME_ASSET_BASE_URLS[type]
                                  ? GAME_ASSET_BASE_URLS[type]
                                  : [
                                        ...Object.values(OPENCODE_BASE_URLS),
                                        OPENROUTER_BASE_URL,
                                        OLLAMA_BASE_URL,
                                        ANTHROPIC_BASE_URL,
                                        MISTRAL_BASE_URL,
                                        COMMANDCODE_BASE_URL,
                                      ].includes(prev.baseUrl)
                                    ? ""
                                    : prev.baseUrl,
              }));
            }}
          >
            <option value="openai-compatible">{t("OpenAI-compatible")}</option>
            <option value="openrouter">{t("OpenRouter")}</option>
            <option value="ollama">{t("Ollama")}</option>
            <option value="anthropic">{t("Anthropic")}</option>
            <option value="claude-agent">{t("Claude Agent")}</option>
            <option value="mistral">{t("Mistral AI")}</option>
            <option value="opencode-go">{t("OpenCode Go")}</option>
            <option value="opencode-zen">{t("OpenCode Zen")}</option>
            <option value="commandcode">{t("CommandCode")}</option>
            <option value="codex-subscription">
              {t("Codex subscription (ChatGPT)")}
            </option>
            <option value="claude-code-subscription">
              {t("Claude Code subscription")}
            </option>
            <option value="meshy">{t("Meshy (3D assets)")}</option>
            <option value="scenario">{t("Scenario (game assets)")}</option>
            <option value="layer">{t("Layer (game assets)")}</option>
            <option value="rodin">{t("Hyper3D Rodin (3D assets)")}</option>
            <option value="tripo">{t("Tripo (3D assets)")}</option>
            <option value="wavespeed">
              {t("WaveSpeedAI (multi-model assets)")}
            </option>
            <option value="retro-diffusion">
              {t("Retro Diffusion (pixel art)")}
            </option>
            {form.type === "custom" && (
              <option value="custom">
                {t("Custom — {{name}}", {
                  name:
                    customDefinitions.find(
                      (definition) => definition.id === form.customProviderId,
                    )?.displayName ||
                    customDefinitions.find(
                      (definition) => definition.id === form.customProviderId,
                    )?.name ||
                    form.customProviderId,
                })}
              </option>
            )}
            {customOptions.map((definition) => (
              <option key={definition.id} value={definition.id}>
                {t("Custom — {{name}}", {
                  name: definition.displayName || definition.name,
                })}
                {definition.enabled ? "" : ` (${t("Draft")})`}
              </option>
            ))}
          </select>
        </label>
        {!form.type.startsWith("opencode") &&
          form.type !== "openrouter" &&
          form.type !== "codex-subscription" &&
          form.type !== "claude-code-subscription" &&
          form.type !== "claude-agent" &&
          form.type !== "commandcode" &&
          form.type !== "custom" && (
            <label>
              <span className="label">{t("Base URL")}</span>
              <input
                className="field"
                placeholder={
                  form.type === "anthropic"
                    ? ANTHROPIC_BASE_URL
                    : form.type === "mistral"
                      ? MISTRAL_BASE_URL
                      : form.type === "ollama"
                        ? OLLAMA_BASE_URL
                        : (GAME_ASSET_BASE_URLS[form.type] ??
                          "https://api.openai.com/v1")
                }
                value={form.baseUrl}
                onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
              />
            </label>
          )}
        {form.type === "commandcode" && (
          <label>
            <span className="label">{t("Base URL")}</span>
            <input
              className="field"
              placeholder={COMMANDCODE_BASE_URL}
              value={form.baseUrl}
              onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
            />
          </label>
        )}
        {form.type !== "codex-subscription" &&
          form.type !== "claude-code-subscription" &&
          form.type !== "custom" && (
            <label>
              <span className="label">
                {provider ? "Replace API key (optional)" : "API key"}
              </span>
              <div className="relative">
                <KeyRound
                  className="absolute top-3 left-3 text-stone-400"
                  size={16}
                />
                <input
                  type="password"
                  className="field pl-9"
                  placeholder={
                    provider?.apiKeyConfigured
                      ? t("Configured — leave blank to keep")
                      : form.type === "ollama"
                        ? "Optional for local Ollama"
                        : "Required"
                  }
                  value={form.apiKey}
                  onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                />
              </div>
            </label>
          )}
        {form.type === "custom" && (
          <>
            <div className="rounded-xl border border-lumen-200 bg-lumen-50 p-3 text-sm text-lumen-900 dark:border-lumen-900/50 dark:bg-lumen-950/30 dark:text-lumen-100">
              {selectedDefinition?.description || t("No description")}
              {selectedDefinition && !selectedDefinition.enabled && (
                <span className="mt-2 block text-[11px] font-semibold tracking-wide text-amber-600 uppercase dark:text-amber-400">
                  {t("Draft — enable this definition before using it")}
                </span>
              )}
            </div>
            {selectedDefinition &&
              Object.keys(
                (
                  selectedDefinition.inputSchema as {
                    properties?: object;
                  } | null
                )?.properties ?? {},
              ).length > 0 && (
                <div className="sm:col-span-2">
                  <span className="label">{t("Instance configuration")}</span>
                  <ConfigFields
                    schema={selectedDefinition.inputSchema ?? {}}
                    value={form.instanceConfig}
                    saved={provider?.instanceConfigConfigured ?? false}
                    onChange={(instanceConfig) =>
                      setForm({ ...form, instanceConfig })
                    }
                  />
                  <p className="mt-2 text-[11px] text-stone-400">
                    {t(
                      "Secret fields are encrypted at rest and never returned to the browser. Leave a secret blank to keep its saved value.",
                    )}
                  </p>
                </div>
              )}
          </>
        )}
        {(form.type === "meshy" ||
          form.type === "scenario" ||
          form.type === "layer" ||
          form.type === "rodin" ||
          form.type === "tripo" ||
          form.type === "wavespeed" ||
          form.type === "retro-diffusion") && (
          <p className="text-xs leading-5 text-stone-500 dark:text-stone-400">
            {form.type === "layer"
              ? t(
                  "Layer base URLs must include your workspace id. Use https://api.app.layer.ai/api/v1/workspaces/<workspace-id>.",
                )
              : form.type === "scenario"
                ? t("Scenario uses API_KEY:API_SECRET in the API key field.")
                : form.type === "tripo"
                  ? t(
                      "Tripo uses a Bearer API key from the Tripo developer platform.",
                    )
                  : form.type === "wavespeed"
                    ? t(
                        "WaveSpeedAI uses a Bearer API key. Add credits to activate API access.",
                      )
                    : form.type === "retro-diffusion"
                      ? t(
                          "Retro Diffusion runs through Replicate; paste a Replicate API token.",
                        )
                      : t(
                          "This provider exposes its compatible 3D, sprite, or tileset models after sync.",
                        )}
          </p>
        )}
        {form.type === "codex-subscription" && (
          <div className="rounded-xl border border-lumen-200 bg-lumen-50 p-3 text-sm text-lumen-900 dark:border-lumen-900/50 dark:bg-lumen-950/30 dark:text-lumen-100">
            {t(
              "Sign in with ChatGPT after saving. Lumen still applies its own\n            organization, model, tool, workspace, and approval permissions.",
            )}
          </div>
        )}
        {form.type === "claude-code-subscription" && (
          <div className="rounded-xl border border-lumen-200 bg-lumen-50 p-3 text-sm text-lumen-900 dark:border-lumen-900/50 dark:bg-lumen-950/30 dark:text-lumen-100">
            {t(
              "Sign in with your own Claude subscription after saving. Claude Code manages sign-in in its isolated account directory, and Lumen routes its tools through your configured workspace permissions.",
            )}
          </div>
        )}
        <label>
          <span className="label">{t("Model policy")}</span>
          <select
            className="field"
            value={form.policy}
            onChange={(e) =>
              setForm({
                ...form,
                policy: e.target.value as ProviderForm["policy"],
              })
            }
          >
            <option value="all">{t("Accept all models")}</option>
            <option value="regex">{t("Match regular expression")}</option>
            <option value="selected">{t("Only selected models")}</option>
          </select>
        </label>
        {form.policy === "regex" && (
          <label className="sm:col-span-2">
            <span className="label">{t("Regular expression")}</span>
            <input
              className="field font-mono"
              value={form.modelRegex}
              onChange={(e) => setForm({ ...form, modelRegex: e.target.value })}
            />
            <p className="mt-2 text-xs text-stone-400">
              {t(
                "This regular expression matches provider model IDs, not display names. Example:",
              )}{" "}
              <code>{t(".*((Free)|(Big Pickle)).*")}</code>
            </p>
          </label>
        )}
        <label className="flex items-center gap-2 text-sm">
          <Toggle
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />{" "}
          {t("Provider enabled")}
        </label>
      </div>
      <div className="mt-4 rounded-xl border border-stone-200 p-4 dark:border-white/10">
        <div className="label">{t("Required model capabilities")}</div>
        <div className="flex flex-wrap gap-4 text-sm">
          {(["vision", "imageGeneration", "tools"] as const).map((key) => (
            <label key={key} className="flex items-center gap-2">
              <Toggle
                checked={form.filterCapabilities.includes(key)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    filterCapabilities: e.target.checked
                      ? [...form.filterCapabilities, key]
                      : form.filterCapabilities.filter(
                          (value) => value !== key,
                        ),
                  })
                }
              />
              {key === "imageGeneration"
                ? "Image output"
                : key[0].toUpperCase() + key.slice(1)}
            </label>
          ))}
        </div>
        <label className="mt-4 block">
          <span className="label">{t("Required output modalities")}</span>
          <input
            className="field"
            placeholder="text, image"
            value={form.filterOutputModalities.join(", ")}
            onChange={(e) =>
              setForm({
                ...form,
                filterOutputModalities: e.target.value
                  .split(",")
                  .map((value) => value.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <p className="mt-2 text-xs text-stone-400">
          {t(
            "Capabilities and modalities are inferred from remote model metadata during sync.",
          )}
        </p>
      </div>
      {form.policy === "selected" && (
        <div className="mt-4">
          <button
            className="button border border-stone-200 dark:border-white/10"
            onClick={() => void discover()}
            disabled={!provider || busy}
          >
            <RefreshCw size={15} /> {t("Discover models")}
          </button>
          {!provider && (
            <span className="ml-3 text-xs text-stone-400">
              {t("Create the provider before selecting remote models.")}
            </span>
          )}
          {discovered.length > 0 && (
            <div className="mt-3 max-h-60 overflow-y-auto rounded-xl border border-stone-200 bg-white p-2 dark:border-white/10 dark:bg-white/5">
              {discovered.map((id) => (
                <label
                  key={id}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-stone-50 dark:hover:bg-white/5"
                >
                  <Toggle
                    checked={form.selectedModels.includes(id)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        selectedModels: e.target.checked
                          ? [...form.selectedModels, id]
                          : form.selectedModels.filter((value) => value !== id),
                      })
                    }
                  />
                  <span className="font-mono text-xs">{id}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      {error && (
        <p className="mt-4 text-sm text-red-600 dark:text-red-300">{error}</p>
      )}
      {codexLogin && savedCodexId && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
          <p className="font-semibold">{t("Finish ChatGPT sign-in")}</p>
          {codexLogin.verificationUrl && (
            <a
              className="mt-2 inline-block underline"
              href={codexLogin.verificationUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t("Open verification page")}
            </a>
          )}
          {codexLogin.userCode && (
            <p className="mt-2 font-mono text-lg">{codexLogin.userCode}</p>
          )}
          <button
            className="button-primary mt-3"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                const status = await api<{ connected: boolean }>(
                  `/providers/${savedCodexId}/codex/status`,
                );
                if (!status.connected)
                  throw new Error("ChatGPT sign-in is not complete yet");
                await api(`/providers/${savedCodexId}/sync`, {
                  method: "POST",
                });
                onSaved();
              } catch (e) {
                setError(
                  e instanceof Error
                    ? e.message
                    : "Codex sign-in could not be verified",
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? (
              <LoaderCircle className="animate-spin" size={16} />
            ) : (
              <Check size={16} />
            )}
            {t("Verify sign-in & sync")}
          </button>
        </div>
      )}
      {claudeCodeLogin && savedClaudeCodeId && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
          <p className="font-semibold">{t("Finish Claude sign-in")}</p>
          {claudeCodeLogin.verificationUrl && (
            <a
              className="mt-2 inline-block underline"
              href={claudeCodeLogin.verificationUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t("Open verification page")}
            </a>
          )}
          {claudeCodeLogin.userCode && (
            <p className="mt-2 font-mono text-lg">{claudeCodeLogin.userCode}</p>
          )}
          <label className="mt-3 block">
            <span className="label">
              {t("Claude sign-in code (if requested)")}
            </span>
            <input
              type="password"
              autoComplete="one-time-code"
              className="field"
              value={claudeLoginCode}
              onChange={(event) => setClaudeLoginCode(event.target.value)}
            />
          </label>
          <button
            className="button mt-2"
            disabled={busy || !claudeLoginCode.trim()}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                await api(
                  `/providers/${savedClaudeCodeId}/claude-code/login/code`,
                  {
                    method: "POST",
                    body: JSON.stringify({ code: claudeLoginCode }),
                  },
                );
                setClaudeLoginCode("");
              } catch (e) {
                setError(
                  e instanceof Error
                    ? e.message
                    : "Claude sign-in code could not be submitted",
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("Submit sign-in code")}
          </button>
          <button
            className="button-primary mt-3"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                const status = await api<{ connected: boolean }>(
                  `/providers/${savedClaudeCodeId}/claude-code/status`,
                );
                if (!status.connected)
                  throw new Error("Claude sign-in is not complete yet");
                await api(`/providers/${savedClaudeCodeId}/sync`, {
                  method: "POST",
                });
                onSaved();
              } catch (e) {
                setError(
                  e instanceof Error
                    ? e.message
                    : "Claude sign-in could not be verified",
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? (
              <LoaderCircle className="animate-spin" size={16} />
            ) : (
              <Check size={16} />
            )}{" "}
            {t("Verify sign-in & sync")}
          </button>
        </div>
      )}
      <div className="mt-5 flex justify-end">
        <button
          className="button-primary"
          disabled={
            busy ||
            !form.name ||
            (form.type === "openai-compatible" && !form.baseUrl) ||
            (form.type === "custom" && !form.customProviderId) ||
            (!provider &&
              !form.apiKey &&
              form.type !== "codex-subscription" &&
              form.type !== "claude-code-subscription" &&
              form.type !== "ollama" &&
              form.type !== "custom")
          }
          onClick={() => void save()}
        >
          {busy ? (
            <LoaderCircle className="animate-spin" size={16} />
          ) : provider ? (
            <Save size={16} />
          ) : (
            <Check size={16} />
          )}{" "}
          {provider ? "Save & sync" : "Connect & sync"}
        </button>
      </div>
    </section>
  );
}
